import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Row,
  Select,
  TimePicker
} from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceLatestConsultationEndpoint } from 'src/api/queries'
import { ConsultationType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { LoadingScreen, Text } from 'src/components'
import { icd10Label } from 'src/features/persons/utils'
import { EXISTING_TREATMENT_PLAN } from 'src/features/persons/utils/constants'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  form: FormInstance
  isEditingPage?: boolean
  /**
   * Editing a performed consultation: the form is prefilled from that consultation, so neither the latest
   * consultation nor the current date/hour are applied.
   */
  skipLatestPrefill?: boolean
}

export const ConsultationGeneralInformationCard = ({
  form,
  isEditingPage,
  skipLatestPrefill
}: Props) => {
  const { t, currentLang } = useLocales()
  const { personId } = useParams()

  const { data: latestConsultation, isLoading } =
    useTreatmentServiceLatestConsultationEndpoint({ personId }, [personId], {
      enabled: !skipLatestPrefill
    })

  const hasLaboratoryInformation = form.getFieldValue('investigationDemandIds')

  const existingTreatmentPlanId = Form.useWatch(EXISTING_TREATMENT_PLAN, {
    form,
    preserve: true
  })

  const hasTreatmentInformation = skipLatestPrefill
    ? !!existingTreatmentPlanId
    : !!latestConsultation?.treatmentPlanId

  const consultationTypeOptions = Object.values(ConsultationType).map(
    (type) => ({
      value: type,
      label: t(`consultation_form.type.${type}`)
    })
  )

  useEffect(() => {
    if (skipLatestPrefill) return

    if (latestConsultation) {
      const { assessment, preliminaryInformation } = latestConsultation

      const latestConsultationDate =
        latestConsultation.generalInformation?.consultationDate
      const currentYear = dayjs().year()
      const latestConsultationYear = latestConsultationDate
        ? dayjs(latestConsultationDate).year()
        : null

      const defaultConsultationType =
        latestConsultationYear === currentYear
          ? ConsultationType.SECONDARY
          : ConsultationType.PRIMARY

      // An onset never entered on the (older) preliminary information is stored as the minimum date.
      const preliminaryOnset =
        preliminaryInformation?.dateOfDiseaseOnset &&
        dayjs(preliminaryInformation.dateOfDiseaseOnset).year() > 1900
          ? dayjs(preliminaryInformation.dateOfDiseaseOnset)
          : undefined

      // The consultation records a single diagnosis; an older consultation that only has the preliminary
      // information carries it over instead.
      const hasFinalDiagnostic = !!assessment || !!preliminaryInformation

      form.setFieldsValue({
        generalInformation: {
          consultationType: defaultConsultationType
        },
        assessment: assessment
          ? {
              comments: assessment.comments,
              dateOfDiseaseOnset: assessment.dateOfDiseaseOnset
                ? dayjs(assessment.dateOfDiseaseOnset)
                : preliminaryOnset,
              aidsClinicalCategoryId:
                assessment.aidsClinicalCategoryId ?? undefined,
              aidsClinicalConditionIds: assessment.aidsClinicalConditionIds
                ?.length
                ? assessment.aidsClinicalConditionIds
                : undefined,
              opportunisticDiseaseIds: assessment.opportunisticDiseases?.map(
                (item) => ({
                  value: item.id,
                  label: item.translation?.[currentLang?.value]
                })
              ),
              finalDiagnosticIds: assessment.finalDiagnostics?.map((item) => ({
                value: item.id,
                label: icd10Label(
                  item.code,
                  item.translation?.[currentLang?.value]
                )
              }))
            }
          : preliminaryInformation
            ? {
                dateOfDiseaseOnset: preliminaryOnset,
                opportunisticDiseaseIds:
                  preliminaryInformation.opportunisticDiseases?.map((item) => ({
                    value: item.id,
                    label: item.translation?.[currentLang?.value]
                  })),
                finalDiagnosticIds:
                  preliminaryInformation.preliminaryDiagnostics?.map(
                    (item) => ({
                      value: item.id,
                      label: icd10Label(
                        item.code,
                        item.translation?.[currentLang?.value]
                      )
                    })
                  )
              }
            : undefined,
        hasTreatment: !!latestConsultation.treatmentPlanId,
        treatmentPlanId: latestConsultation?.treatmentPlanId,
        latestConsultationId: latestConsultation.id,
        lastConsultationDate:
          latestConsultation.generalInformation?.consultationDate,
        latestConsultation: latestConsultation,
        hasFinalDiagnostic
      })
    } else {
      // No latest consultation (new patient), default to PRIMARY
      form.setFieldsValue({
        generalInformation: {
          consultationType: ConsultationType.PRIMARY
        }
      })
    }
  }, [latestConsultation])

  useEffect(() => {
    if (skipLatestPrefill) return

    form.setFieldsValue({
      generalInformation: {
        consultationDate: dayjs(),
        consultationHour: dayjs()
      }
    })
  }, [])

  if (isLoading && !skipLatestPrefill) {
    return <LoadingScreen />
  }
  return (
    <Card title={t('users.general_info')}>
      <Row gutter={[24, 24]}>
        <Form.Item
          name={['generalInformation', 'consultationType']}
          rules={requiredRules}
          label={t('consultations.consultation_type')}
          hidden
        >
          <Select
            options={consultationTypeOptions}
            placeholder={t('consultations.consultation_type')}
          />
        </Form.Item>

        <Col span={12}>
          <Form.Item
            name={['generalInformation', 'consultationDate']}
            label={t('consultations.consultation_date')}
            rules={requiredRules}
          >
            <DatePicker
              disabled={isEditingPage}
              format={dayMonthYearFormatWithDot}
              className="full-width"
              disabledDate={disabledFutureDate}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['generalInformation', 'consultationHour']}
            label={t('common.hour')}
            rules={requiredRules}
          >
            <TimePicker
              disabled={isEditingPage}
              showSecond={false}
              needConfirm={false}
              className="full-width"
            />
          </Form.Item>
          <Form.Item name="latestConsultation" hidden />
        </Col>

        <Col span={24}>
          <Flex vertical gap={24}>
            <Text size="xl" weight={700}>
              {t('consultations.select_options')}
            </Text>

            <Flex wrap="wrap" gap={16}>
              <Form.Item name="hasExamination" valuePropName="checked">
                <Checkbox>{t('consultations.examination')}</Checkbox>
              </Form.Item>

              <Form.Item name="hasFinalDiagnostic" valuePropName="checked">
                <Checkbox>{t('consultations.final_diagnostic')}</Checkbox>
              </Form.Item>

              <Form.Item name="hasLaboratory" valuePropName="checked">
                <Checkbox disabled={!!hasLaboratoryInformation}>
                  {t('organizational_structure.lab')}
                </Checkbox>
              </Form.Item>

              <Form.Item name="hasTreatment" valuePropName="checked">
                <Checkbox disabled={hasTreatmentInformation}>
                  {t('common.treatment')}
                </Checkbox>
              </Form.Item>

              <Form.Item name="hasContacts" valuePropName="checked">
                <Checkbox>{t('consultations.contacts')}</Checkbox>
              </Form.Item>
            </Flex>
          </Flex>
        </Col>
      </Row>
    </Card>
  )
}
